import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Shield, FileText, CheckCircle2, AlertTriangle, XCircle,
  Download, RefreshCw, Loader2, BarChart3, Clock, Award,
  Users, Target, BookOpen, ClipboardCheck, Scale, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useAuth } from '../context/AuthContext';

const STATUS_ICONS = {
  PASS: CheckCircle2,
  PARTIAL: AlertTriangle,
  FAIL: XCircle
};

const STATUS_COLORS = {
  PASS: 'text-emerald-600 bg-emerald-50',
  PARTIAL: 'text-amber-600 bg-amber-50',
  FAIL: 'text-red-600 bg-red-50'
};

export default function ComplianceReportPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [programHealth, setProgramHealth] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [reportRes, healthRes] = await Promise.all([
        api.get('/bia-engine/audit/compliance-report').catch(() => ({ data: null })),
        api.get('/bia-engine/audit/program-health')
      ]);
      
      setReport(reportRes.data);
      setProgramHealth(healthRes.data);
    } catch (err) {
      console.error('Error fetching compliance data:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const res = await api.get('/bia-engine/audit/compliance-report');
      setReport(res.data);
      toast.success('Compliance report generated');
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="compliance-loading">
        <Loader2 className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="compliance-report-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Scale className="w-7 h-7 text-sky-700" />
            Compliance & Audit
          </h1>
          <p className="text-slate-500 mt-1">ISO 22301 compliance evidence and program health monitoring</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleGenerateReport} disabled={generating} data-testid="generate-report-btn">
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Generate Report
          </Button>
        </div>
      </div>

      {/* Program Health Overview */}
      {programHealth && (
        <Card className={`border-2 ${
          programHealth.health_status === 'Healthy' ? 'border-emerald-200' :
          programHealth.health_status === 'Attention Needed' ? 'border-amber-200' : 'border-red-200'
        }`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Program Health Dashboard
                </CardTitle>
                <CardDescription>Real-time BCM program health monitoring</CardDescription>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{programHealth.overall_health_score}%</p>
                <Badge className={
                  programHealth.health_status === 'Healthy' ? 'bg-emerald-100 text-emerald-700' :
                  programHealth.health_status === 'Attention Needed' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                }>
                  {programHealth.health_status}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {Object.entries(programHealth.metrics).map(([key, metric]) => (
                <div key={key} className={`p-4 rounded-lg ${
                  metric.status === 'good' ? 'bg-emerald-50' :
                  metric.status === 'warning' ? 'bg-amber-50' : 'bg-red-50'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-500">{metric.value}%</span>
                    {metric.status === 'good' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> :
                     metric.status === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-500" /> :
                     <XCircle className="w-4 h-4 text-red-500" />}
                  </div>
                  <Progress value={typeof metric.value === 'number' ? metric.value : 0} className="h-2 mb-2" />
                  <p className="text-sm font-medium">{key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</p>
                  <p className="text-xs text-slate-500 mt-1">{metric.detail}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Compliance Report */}
      {report && (
        <Tabs defaultValue="summary" className="space-y-4">
          <TabsList className="bg-white border">
            <TabsTrigger value="summary" data-testid="summary-tab">
              <BarChart3 className="w-4 h-4 mr-2" />
              Summary
            </TabsTrigger>
            <TabsTrigger value="evidence" data-testid="evidence-tab">
              <FileText className="w-4 h-4 mr-2" />
              Evidence
            </TabsTrigger>
            <TabsTrigger value="iso-alignment" data-testid="iso-tab">
              <Shield className="w-4 h-4 mr-2" />
              ISO 22301 Alignment
            </TabsTrigger>
            <TabsTrigger value="recommendations" data-testid="recommendations-tab">
              <Target className="w-4 h-4 mr-2" />
              Recommendations
            </TabsTrigger>
          </TabsList>

          {/* Summary Tab */}
          <TabsContent value="summary">
            <Card>
              <CardHeader>
                <CardTitle>Compliance Summary</CardTitle>
                <CardDescription>
                  Report generated: {new Date(report.generated_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className={`w-32 h-32 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      report.compliance_summary.color === 'green' ? 'bg-emerald-100' :
                      report.compliance_summary.color === 'yellow' ? 'bg-amber-100' : 'bg-red-100'
                    }`}>
                      <span className={`text-4xl font-bold ${
                        report.compliance_summary.color === 'green' ? 'text-emerald-600' :
                        report.compliance_summary.color === 'yellow' ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {report.compliance_summary.score}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold">{report.compliance_summary.status}</h3>
                    <p className="text-slate-500">Score: {report.compliance_summary.score} / {report.compliance_summary.max_score}</p>
                    {report.certification_readiness && (
                      <Badge className="mt-3 bg-emerald-100 text-emerald-700">
                        <Award className="w-3 h-3 mr-1" />
                        Certification Ready
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <FileText className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                    <p className="text-2xl font-bold">{report.evidence_sections.bia.total_processes}</p>
                    <p className="text-xs text-slate-500">BIA Processes</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <AlertTriangle className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                    <p className="text-2xl font-bold">{report.evidence_sections.risk_assessment.total_risks}</p>
                    <p className="text-xs text-slate-500">Risk Assessments</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <Users className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                    <p className="text-2xl font-bold">{report.evidence_sections.training.total_assignments}</p>
                    <p className="text-xs text-slate-500">Training Assignments</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <ClipboardCheck className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                    <p className="text-2xl font-bold">{report.evidence_sections.audit_trail.total_audit_entries}</p>
                    <p className="text-xs text-slate-500">Audit Entries</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Evidence Tab */}
          <TabsContent value="evidence">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Governance Evidence */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Shield className="w-4 h-4 text-sky-700" />
                    Governance Evidence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">BCM Policy Status</span>
                    <Badge className={report.evidence_sections.governance.bcm_policy_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {report.evidence_sections.governance.bcm_policy_status}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Authority Matrix Defined</span>
                    {report.evidence_sections.governance.authority_matrix_defined ? 
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" /> :
                      <XCircle className="w-5 h-5 text-red-500" />}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Defined Roles</span>
                    <span className="font-medium">{report.evidence_sections.governance.roles_count}</span>
                  </div>
                </CardContent>
              </Card>

              {/* BIA Evidence */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-sky-700" />
                    BIA Evidence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Completion Rate</span>
                    <span className="font-medium">{report.evidence_sections.bia.completion_rate}%</span>
                  </div>
                  <Progress value={report.evidence_sections.bia.completion_rate} className="h-2" />
                  <div className="flex justify-between items-center text-sm text-slate-500">
                    <span>Total Processes: {report.evidence_sections.bia.total_processes}</span>
                    <span>Critical: {report.evidence_sections.bia.critical_processes}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Risk Evidence */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-sky-700" />
                    Risk Assessment Evidence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Treatment Rate</span>
                    <span className="font-medium">{report.evidence_sections.risk_assessment.treatment_rate}%</span>
                  </div>
                  <Progress value={report.evidence_sections.risk_assessment.treatment_rate} className="h-2" />
                  <div className="flex justify-between items-center text-sm text-slate-500">
                    <span>HIGH Risks: {report.evidence_sections.risk_assessment.high_risks}</span>
                    <span>Pending: {report.evidence_sections.risk_assessment.pending_treatment}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Training Evidence */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-sky-700" />
                    Training Evidence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Completion Rate</span>
                    <span className="font-medium">{report.evidence_sections.training.completion_rate}%</span>
                  </div>
                  <Progress value={report.evidence_sections.training.completion_rate} className="h-2" />
                  <div className="flex justify-between items-center text-sm text-slate-500">
                    <span>Total: {report.evidence_sections.training.total_assignments}</span>
                    <span>Certified: {report.evidence_sections.training.certified_count}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Exercise Evidence */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-sky-700" />
                    Exercise Evidence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Completed Exercises</span>
                    <span className="font-medium">{report.evidence_sections.exercises.completed_exercises}</span>
                  </div>
                  <div className="text-sm text-slate-500">
                    <p>Last Exercise: {report.evidence_sections.exercises.last_exercise_date ? 
                      new Date(report.evidence_sections.exercises.last_exercise_date).toLocaleDateString() : 'None'}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Audit Trail Evidence */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4 text-sky-700" />
                    Audit Trail Evidence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Total Audit Entries</span>
                    <span className="font-medium">{report.evidence_sections.audit_trail.total_audit_entries}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Approvals Logged</span>
                    <span className="font-medium">{report.evidence_sections.audit_trail.approvals_count}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Digital Sign-offs</span>
                    <span className="font-medium">{report.evidence_sections.digital_signoffs.total_signoffs}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ISO Alignment Tab */}
          <TabsContent value="iso-alignment">
            <Card>
              <CardHeader>
                <CardTitle>ISO 22301:2019 Clause Alignment</CardTitle>
                <CardDescription>
                  Assessment against ISO 22301 Business Continuity Management System requirements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ISO Clause</TableHead>
                      <TableHead>Requirement</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { clause: '4', name: 'Context of the Organization', key: 'clause_4_context' },
                      { clause: '5', name: 'Leadership', key: 'clause_5_leadership' },
                      { clause: '6', name: 'Planning', key: 'clause_6_planning' },
                      { clause: '7', name: 'Support (Competence, Training)', key: 'clause_7_support' },
                      { clause: '8', name: 'Operation (BIA, RA, Plans)', key: 'clause_8_operation' },
                      { clause: '9', name: 'Performance Evaluation (Exercises)', key: 'clause_9_evaluation' },
                      { clause: '10', name: 'Improvement (Audit, Review)', key: 'clause_10_improvement' }
                    ].map(item => {
                      const status = report.iso_22301_alignment[item.key];
                      const Icon = STATUS_ICONS[status];
                      
                      return (
                        <TableRow key={item.clause}>
                          <TableCell className="font-mono">Clause {item.clause}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>
                            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${STATUS_COLORS[status]}`}>
                              <Icon className="w-4 h-4" />
                              {status}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations">
            <Card>
              <CardHeader>
                <CardTitle>Audit Recommendations</CardTitle>
                <CardDescription>
                  Prioritized actions to improve compliance posture
                </CardDescription>
              </CardHeader>
              <CardContent>
                {report.recommendations.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                    <p className="text-emerald-600 font-medium">No recommendations</p>
                    <p className="text-sm text-slate-500">Your BCM program meets all compliance requirements</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {report.recommendations.map((rec, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                        rec.priority === 'HIGH' ? 'border-l-red-500 bg-red-50' : 'border-l-amber-500 bg-amber-50'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant={rec.priority === 'HIGH' ? 'destructive' : 'secondary'}>
                                {rec.priority}
                              </Badge>
                              <span className="font-medium">{rec.area}</span>
                            </div>
                            <p className="text-sm text-slate-700">{rec.finding}</p>
                            <p className="text-sm text-slate-600 mt-2">
                              <strong>Recommendation:</strong> {rec.recommendation}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs whitespace-nowrap">
                            {rec.iso_reference}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* No Report State */}
      {!report && (
        <Card>
          <CardContent className="py-12 text-center">
            <Scale className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-2">No compliance report generated yet</p>
            <p className="text-sm text-slate-400 mb-4">
              Click &quot;Generate Report&quot; to create a comprehensive compliance evidence report
            </p>
            <Button onClick={handleGenerateReport} disabled={generating}>
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Generate Compliance Report
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
